import React, { useEffect, useState, useCallback } from "react";
import {
  Table, Button, Modal, Form, DatePicker, Input, InputNumber,
  Select, Space, message, Divider, Tag, Typography, Tooltip, Descriptions,
} from "antd";
import { PlusOutlined, DeleteOutlined, InfoCircleOutlined, EyeOutlined, SearchOutlined } from "@ant-design/icons";
import { scrap, products as prodApi, inventory } from "../api";
import PageHeader from "../components/common/PageHeader";
import { fmtDate, fmtCurrency } from "../utils/format";
import dayjs from "dayjs";

const { RangePicker } = DatePicker;
const { Text } = Typography;

export default function Scrap() {
  const [data, setData]       = useState({ content: [], totalElements: 0 });
  const [loading, setLoading] = useState(false);
  const [modal, setModal]     = useState(false);
  const [allProds, setAllProds] = useState([]);
  const [stockMap, setStockMap] = useState({});
  const [lines, setLines]     = useState([{ productId: null, quantity: 1, maxQty: null }]);
  const [page, setPage]       = useState(0);
  const [form] = Form.useForm();

  // ── Filters ───────────────────────────────────────────────────
  const [q, setQ]             = useState("");
  const [dateRange, setDateRange] = useState([]);
  const [viewRec, setViewRec] = useState(null);

  const load = useCallback(async (pg = page, search = q, range = dateRange) => {
    setLoading(true);
    try {
      const r = await scrap.list({
        page: pg, size: 20,
        q: search || undefined,
        fromDate: range?.length === 2 ? range[0].format("YYYY-MM-DD") : undefined,
        toDate:   range?.length === 2 ? range[1].format("YYYY-MM-DD") : undefined,
      });
      setData(r.data.data);
    } finally { setLoading(false); }
  }, [page, q, dateRange]);

  useEffect(() => { load(); }, [page]);

  const handleSearch = (val) => { setQ(val); setPage(0); load(0, val, dateRange); };
  const handleDateRange = (range) => { setDateRange(range || []); setPage(0); load(0, q, range || []); };

  useEffect(() => {
    prodApi.all().then((r) => setAllProds(r.data.data));
    // Load full stock map for available qty enforcement
    inventory.stock().then((r) => {
      const map = {};
      (r.data.data?.content || r.data.data || []).forEach((s) => {
        map[s.productId] = Number(s.available ?? s.stock ?? 0);
      });
      setStockMap(map);
    }).catch(() => {});
  }, []);

  const onProductSelect = (i, v) => {
    const ls = [...lines];
    ls[i].productId = v;
    ls[i].maxQty    = stockMap[v] ?? null;
    ls[i].quantity  = 1;
    setLines(ls);
  };

  const onQtyChange = (i, v) => {
    const ls = [...lines];
    ls[i].quantity = v;
    setLines(ls);
  };

  const onSave = async () => {
    try {
      const vals = await form.validateFields();
      const validLines = lines.filter((l) => l.productId && l.quantity > 0);
      if (validLines.length === 0) { message.error("Add at least one item"); return; }
      // Validate max qty
      for (const l of validLines) {
        if (l.maxQty !== null && l.quantity > l.maxQty) {
          message.error(`Quantity for a product exceeds available stock (max ${l.maxQty})`);
          return;
        }
      }
      await scrap.create({
        ...vals,
        date: vals.date.format("YYYY-MM-DD"),
        items: validLines.map(({ productId, quantity }) => ({ productId, quantity })),
      });
      message.success("Scrap entry saved");
      setModal(false);
      load();
    } catch { message.error("Error saving scrap"); }
  };

  const openModal = () => {
    form.resetFields();
    form.setFieldsValue({ date: dayjs() });
    setLines([{ productId: null, quantity: 1, maxQty: null }]);
    setModal(true);
  };

  // Table: expand row to show items as a nested table
  const expandedRowRender = (record) => {
    const items = record.items || [];
    if (items.length === 0) return <Text type="secondary">No items</Text>;
    return (
      <Table
        size="small"
        pagination={false}
        rowKey="id"
        dataSource={items}
        columns={[
          { title: "Product", render: (_, r) => <span className="mono">{r.product?.name || "—"}</span> },
          { title: "Unit", render: (_, r) => r.product?.unit || "—" },
          { title: "Quantity", dataIndex: "quantity", render: (v) => <strong>{v}</strong> },
        ]}
        style={{ margin: "0 0 0 24px" }}
      />
    );
  };

  const cols = [
    { title: "Scrap No",  dataIndex: "scrapNo",    render: (v) => <span className="mono text-danger">{v}</span> },
    { title: "Date",      dataIndex: "date",        render: fmtDate },
    { title: "Authority", dataIndex: "authority",   render: (v) => v || "—" },
    {
      title: "Items",
      dataIndex: "items",
      render: (items) =>
        (items || []).map((i) => (
          <Tag key={i.id} color="red" style={{ marginBottom: 2 }}>
            {i.product?.name} × {i.quantity}
          </Tag>
        )),
    },
    { title: "Notes", dataIndex: "notes", render: (v) => v || "—" },
    {
      title: "Action", key: "action",
      render: (_, r) => (
        <Button size="small" icon={<EyeOutlined />} onClick={() => setViewRec(r)}>View</Button>
      ),
    },
  ];

  return (
    <div>
      <PageHeader
        title="Scrap"
        subtitle="Record damaged or scrapped items"
        actions={[
          <Button key="a" type="primary" danger icon={<PlusOutlined />} onClick={openModal}>
            New Scrap Entry
          </Button>,
        ]}
      />

      {/* ── Filters ──────────────────────────────────────── */}
      <Space wrap style={{ marginBottom: 16 }}>
        <Input
          prefix={<SearchOutlined />}
          placeholder="Search scrap no, authority, notes..."
          value={q}
          onChange={(e) => handleSearch(e.target.value)}
          allowClear
          style={{ width: 280 }}
        />
        <RangePicker
          value={dateRange}
          onChange={handleDateRange}
          style={{ width: 260 }}
        />
      </Space>

      <Table
        scroll={{ x: "max-content" }}
        columns={cols}
        dataSource={data.content}
        rowKey="id"
        loading={loading}
        size="small"
        expandable={{ expandedRowRender, rowExpandable: (r) => (r.items || []).length > 0 }}
        pagination={{
          total: data.totalElements,
          pageSize: 20,
          current: page + 1,
          onChange: (p) => setPage(p - 1),
        }}
      />

      {/* ── View Scrap Modal ──────────────────────────────── */}
      <Modal
        open={!!viewRec}
        title={`Scrap Entry — ${viewRec?.scrapNo}`}
        onCancel={() => setViewRec(null)}
        footer={<Button onClick={() => setViewRec(null)}>Close</Button>}
        width={520}
      >
        {viewRec && (
          <>
            <Descriptions bordered size="small" column={1} style={{ marginBottom: 12 }}>
              <Descriptions.Item label="Scrap No">{viewRec.scrapNo}</Descriptions.Item>
              <Descriptions.Item label="Date">{fmtDate(viewRec.date)}</Descriptions.Item>
              <Descriptions.Item label="Authority">{viewRec.authority || "—"}</Descriptions.Item>
              <Descriptions.Item label="Notes">{viewRec.notes || "—"}</Descriptions.Item>
            </Descriptions>
            <Divider style={{ borderColor: "var(--border)", margin: "8px 0" }}>Items Scrapped</Divider>
            <Table
              size="small"
              rowKey="id"
              pagination={false}
              dataSource={viewRec.items || []}
              columns={[
                { title: "Product", dataIndex: ["product", "name"] },
                { title: "Unit", dataIndex: ["product", "unit"] },
                { title: "Quantity", dataIndex: "quantity" },
              ]}
            />
          </>
        )}
      </Modal>

      <Modal
        open={modal}
        title="New Scrap Entry"
        onCancel={() => setModal(false)}
        onOk={onSave}
        okText="Save"
        okButtonProps={{ danger: true }}
        width={560}
      >
        <Form form={form} layout="vertical">
          <Space>
            <Form.Item name="date" label="Date" rules={[{ required: true }]}>
              <DatePicker defaultValue={dayjs()} />
            </Form.Item>
            <Form.Item name="authority" label="Authority">
              <Input style={{ width: 180 }} />
            </Form.Item>
          </Space>
          <Form.Item name="notes" label="Notes">
            <Input.TextArea rows={2} />
          </Form.Item>
        </Form>

        <Divider style={{ borderColor: "var(--border)" }}>Items</Divider>

        {lines.map((l, i) => (
          <Space key={i} style={{ marginBottom: 8 }} align="center">
            <Select
              placeholder="Product"
              style={{ width: 220 }}
              showSearch
              optionFilterProp="label"
              value={l.productId}
              onChange={(v) => onProductSelect(i, v)}
              options={allProds.map((p) => ({
                value: p.id,
                label: `${p.name} (${p.unit})`,
              }))}
            />
            <InputNumber
              min={0.001}
              max={l.maxQty ?? undefined}
              step={0.5}
              value={l.quantity}
              onChange={(v) => onQtyChange(i, v)}
              style={{ width: 110 }}
            />
            {l.maxQty !== null && (
              <Tooltip title={`Available stock: ${l.maxQty}`}>
                <Text
                  type={l.quantity > l.maxQty ? "danger" : "secondary"}
                  style={{ fontSize: 11, whiteSpace: "nowrap" }}
                >
                  <InfoCircleOutlined style={{ marginRight: 3 }} />
                  max {l.maxQty}
                </Text>
              </Tooltip>
            )}
            {lines.length > 1 && (
              <Button
                icon={<DeleteOutlined />}
                size="small"
                danger
                onClick={() => setLines(lines.filter((_, idx) => idx !== i))}
              />
            )}
          </Space>
        ))}

        <Button
          icon={<PlusOutlined />}
          size="small"
          style={{ marginTop: 4 }}
          onClick={() => setLines([...lines, { productId: null, quantity: 1, maxQty: null }])}
        >
          Add Item
        </Button>
      </Modal>
    </div>
  );
}